import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUserTie, faSearch, faEdit, faClock, faBan,
    faCheckCircle, faRocket, faGem, faBriefcase,
    faChevronDown, faSync, faExclamationTriangle, faShieldAlt
} from '@fortawesome/free-solid-svg-icons';
import { Modal, Spinner, Form } from 'react-bootstrap';
import request from '../../../../request';
import Notify from '../../../compartidos/Notify';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/';

const PLAN_META = {
    1: { label: 'Trial', css: 'sa-badge-trial', icon: faRocket },
    2: { label: 'Premium', css: 'sa-badge-premium', icon: faGem },
    3: { label: 'Business', css: 'sa-badge-business', icon: faBriefcase },
};

const TRIAL_OPTS = [
    { label: '+7 días', days: 7 },
    { label: '+14 días', days: 14 },
    { label: '+30 días', days: 30 },
    { label: '+60 días', days: 60 },
];

/**
 * SuperAdminUsers — List all platform admins with inline plan/trial/status management.
 */
const SuperAdminUsers = () => {
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [modal, setModal] = useState(null);   // { type:'plan'|'trial'|'status', admin }
    const [saving, setSaving] = useState(false);
    const [planSel, setPlanSel] = useState(1);
    const [trialDays, setTrialDays] = useState(14);
    const [profiles, setProfiles] = useState([]);
    const [profileSel, setProfileSel] = useState('');

    useEffect(() => {
        fetchAdmins();
        fetchProfiles();
    }, []);

    const fetchProfiles = () => {
        request.Get(API_BASE + 'superadmin/profiles', {}, r => {
            if (r.success) setProfiles(r.data || []);
        });
    };

    const fetchAdmins = () => {
        setLoading(true);
        request.Get(API_BASE + 'superadmin/admins', {}, r => {
            if (r.success) setAdmins(r.data || []);
            else Notify({ type: 'fail', text: 'Error al cargar administradores' });
            setLoading(false);
        }, 'json', () => setLoading(false));
    };

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        return admins.filter(a =>
            !q ||
            a.AdminNombre?.toLowerCase().includes(q) ||
            a.AdminEmail?.toLowerCase().includes(q) ||
            a.EmpresaNombre?.toLowerCase().includes(q)
        );
    }, [admins, search]);

    const openModal = (type, admin) => {
        setPlanSel(admin.PlanId || 1);
        setTrialDays(14);
        setProfileSel(admin.ProfileId || '');
        setModal({ type, admin });
    };
    const closeModal = () => { if (!saving) setModal(null); };

    const handleSavePlan = () => {
        setSaving(true);
        request.Put(API_BASE + `superadmin/admins/${modal.admin.AdminId}/plan`, { planId: planSel }, r => {
            if (r.success) {
                Notify({ type: 'success', text: 'Plan actualizado correctamente' });
                setAdmins(prev => prev.map(a => a.AdminId === modal.admin.AdminId
                    ? { ...a, PlanId: planSel, PlanNombre: PLAN_META[planSel]?.label }
                    : a
                ));
                closeModal();
            } else {
                Notify({ type: 'fail', text: r.msg || 'Error al actualizar plan' });
            }
            setSaving(false);
        }, 'json', () => { setSaving(false); });
    };

    const handleExtendTrial = () => {
        setSaving(true);
        request.Put(API_BASE + `superadmin/admins/${modal.admin.AdminId}/extend-trial`, { days: trialDays }, r => {
            if (r.success) {
                Notify({ type: 'success', text: `Trial extendido ${trialDays} días` });
                setAdmins(prev => prev.map(a => a.AdminId === modal.admin.AdminId
                    ? { ...a, TrialExpiry: r.newExpiry }
                    : a
                ));
                closeModal();
            } else {
                Notify({ type: 'fail', text: r.msg || 'Error al extender trial' });
            }
            setSaving(false);
        }, 'json', () => { setSaving(false); });
    };

    const handleToggleStatus = () => {
        setSaving(true);
        const newStatus = modal.admin.AdminActivo ? 0 : 1;
        request.Put(API_BASE + `superadmin/admins/${modal.admin.AdminId}/status`, { active: newStatus }, r => {
            if (r.success) {
                Notify({ type: 'success', text: newStatus ? 'Admin reactivado' : 'Admin suspendido' });
                setAdmins(prev => prev.map(a => a.AdminId === modal.admin.AdminId
                    ? { ...a, AdminActivo: newStatus }
                    : a
                ));
                closeModal();
            } else {
                Notify({ type: 'fail', text: r.msg || 'Error al cambiar estado' });
            }
            setSaving(false);
        }, 'json', () => { setSaving(false); });
    };

    const handleSaveProfile = () => {
        setSaving(true);
        request.Put(API_BASE + `superadmin/admins/${modal.admin.AdminId}/profile`, { profileId: profileSel }, r => {
            if (r.success) {
                Notify({ type: 'success', text: 'Perfil asignado correctamente' });
                setAdmins(prev => prev.map(a => a.AdminId === modal.admin.AdminId
                    ? { ...a, ProfileId: profileSel, ProfileName: profiles.find(p => p.ProfileId === parseInt(profileSel))?.ProfileName }
                    : a
                ));
                closeModal();
            } else {
                Notify({ type: 'fail', text: r.msg || 'Error al asignar perfil' });
            }
            setSaving(false);
        }, 'json', () => setSaving(false));
    };

    const trialDaysLeft = (expiry) => {
        if (!expiry) return null;
        const diff = Math.ceil((new Date(expiry) - new Date()) / (1000 * 60 * 60 * 24));
        return diff;
    };

    return (
        <div className="animate-in pb-5">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <div>
                    <h2 style={{ fontWeight: 800, margin: 0 }}>
                        <FontAwesomeIcon icon={faUserTie} style={{ color: 'var(--sa-primary)', marginRight: '0.6rem' }} />
                        Administradores
                    </h2>
                    <p className="text-muted small mt-1 mb-0">Gestión global de todos los admins de la plataforma</p>
                </div>
                <div className="d-flex gap-2">
                    <button className="sa-btn sa-btn-ghost" onClick={fetchAdmins} disabled={loading}>
                        <FontAwesomeIcon icon={faSync} spin={loading} />
                        Actualizar
                    </button>
                </div>
            </div>

            {/* Search */}
            <div className="sa-search-bar mb-4" style={{ maxWidth: '100%' }}>
                <FontAwesomeIcon icon={faSearch} style={{ color: '#475569' }} />
                <input
                    placeholder="Buscar por nombre, email o empresa..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
                {search && (
                    <span style={{ color: '#475569', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                        {filtered.length} resultado{filtered.length !== 1 ? 's' : ''}
                    </span>
                )}
            </div>

            {/* Table */}
            <div className="sa-panel">
                {loading ? (
                    <div className="text-center py-5">
                        <Spinner animation="grow" style={{ color: 'var(--sa-primary)' }} />
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="sa-empty">
                        <FontAwesomeIcon icon={faUserTie} style={{ fontSize: '3rem', opacity: 0.15, display: 'block', margin: '0 auto 1rem' }} />
                        <p>No se encontraron administradores</p>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="sa-table">
                            <thead>
                                <tr>
                                    <th>Administrador</th>
                                    <th>Empresa</th>
                                    <th>Plan / Perfil</th>
                                    <th>Vencimiento Trial</th>
                                    <th>Estado</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map(admin => {
                                    const plan = PLAN_META[admin.PlanId] || PLAN_META[1];
                                    const left = trialDaysLeft(admin.TrialExpiry);
                                    const urgent = left !== null && left <= 3;
                                    return (
                                        <tr key={admin.AdminId}>
                                            <td>
                                                <div className="d-flex flex-column">
                                                    <span className="font-weight-bold" style={{ fontSize: '0.9rem' }}>
                                                        {admin.AdminNombre} {admin.AdminApellido}
                                                    </span>
                                                    <small style={{ color: '#64748b' }}>{admin.AdminEmail}</small>
                                                </div>
                                            </td>
                                            <td>
                                                <span style={{ color: '#94a3b8', fontSize: '0.87rem' }}>
                                                    {admin.EmpresaNombre || <em style={{ opacity: 0.4 }}>Sin empresa</em>}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="d-flex flex-column gap-1">
                                                    <span className={`sa-badge ${plan.css}`}>
                                                        <FontAwesomeIcon icon={plan.icon} />
                                                        {plan.label}
                                                    </span>
                                                    <span className="sa-badge sa-badge-ghost" style={{ fontSize: '0.65rem', border: '1px solid rgba(255,255,255,0.05)' }}>
                                                        Perfil: {admin.ProfileName || <em style={{ opacity: 0.5 }}>Sin asignar</em>}
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                {left !== null ? (
                                                    <span style={{ fontSize: '0.82rem', color: urgent ? '#fca5a5' : '#94a3b8' }}>
                                                        {urgent && <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: 4 }} />}
                                                        {left > 0 ? `${left} días` : 'Vencido'}
                                                    </span>
                                                ) : (
                                                    <span style={{ color: '#334155', fontSize: '0.82rem' }}>N/A</span>
                                                )}
                                            </td>
                                            <td>
                                                <span className={`sa-badge ${admin.AdminActivo ? 'sa-badge-success' : 'sa-badge-danger'}`}>
                                                    <FontAwesomeIcon icon={admin.AdminActivo ? faCheckCircle : faBan} />
                                                    {admin.AdminActivo ? 'Activo' : 'Suspendido'}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="d-flex gap-2 flex-wrap">
                                                    <button
                                                        className="sa-btn sa-btn-ghost sa-btn-sm"
                                                        onClick={() => openModal('plan', admin)}
                                                        title="Cambiar plan"
                                                    >
                                                        <FontAwesomeIcon icon={faEdit} /> Plan
                                                    </button>
                                                    <button
                                                        className="sa-btn sa-btn-primary sa-btn-sm"
                                                        onClick={() => openModal('profile', admin)}
                                                        title="Asignar Perfil"
                                                    >
                                                        <FontAwesomeIcon icon={faShieldAlt} /> Perfil
                                                    </button>
                                                    <button
                                                        className="sa-btn sa-btn-amber sa-btn-sm"
                                                        onClick={() => openModal('trial', admin)}
                                                        title="Extender trial"
                                                    >
                                                        <FontAwesomeIcon icon={faClock} /> Trial
                                                    </button>
                                                    <button
                                                        className={`sa-btn sa-btn-sm ${admin.AdminActivo ? 'sa-btn-danger' : 'sa-btn-success'}`}
                                                        onClick={() => openModal('status', admin)}
                                                    >
                                                        <FontAwesomeIcon icon={admin.AdminActivo ? faBan : faCheckCircle} />
                                                        {admin.AdminActivo ? 'Suspender' : 'Activar'}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* ── Modal: Change Plan ── */}
            <Modal show={modal?.type === 'plan'} onHide={closeModal} centered dialogClassName="sa-modal">
                <Modal.Header closeButton className="border-0">
                    <Modal.Title style={{ fontWeight: 800, fontSize: '1.1rem', color: '#f8fafc' }}>
                        <FontAwesomeIcon icon={faEdit} style={{ color: 'var(--sa-primary)', marginRight: '0.5rem' }} />
                        Cambiar Plan — {modal?.admin?.AdminNombre}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: '1.5rem' }}>
                        Plan actual: <strong style={{ color: '#f8fafc' }}>{PLAN_META[modal?.admin?.PlanId]?.label || 'Trial'}</strong>
                    </p>
                    <div className="d-flex flex-column gap-3">
                        {Object.entries(PLAN_META).map(([id, p]) => (
                            <label key={id} style={{
                                display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem 1.25rem',
                                borderRadius: '12px', cursor: 'pointer',
                                background: parseInt(id) === planSel ? 'rgba(168,85,247,0.1)' : 'rgba(255,255,255,0.02)',
                                border: `1px solid ${parseInt(id) === planSel ? 'rgba(168,85,247,0.4)' : 'rgba(255,255,255,0.06)'}`,
                                transition: 'all 0.2s'
                            }}>
                                <input type="radio" hidden value={id} checked={planSel === parseInt(id)} onChange={() => setPlanSel(parseInt(id))} />
                                <FontAwesomeIcon icon={p.icon} style={{ color: parseInt(id) === planSel ? 'var(--sa-primary)' : '#475569', fontSize: '1.1rem' }} />
                                <span style={{ fontWeight: 600, color: parseInt(id) === planSel ? '#f8fafc' : '#64748b' }}>{p.label}</span>
                                {parseInt(id) === (modal?.admin?.PlanId || 1) && (
                                    <span className="sa-badge sa-badge-success ms-auto" style={{ marginLeft: 'auto' }}>Actual</span>
                                )}
                            </label>
                        ))}
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <button className="sa-btn sa-btn-ghost" onClick={closeModal}>Cancelar</button>
                    <button className="sa-btn sa-btn-primary" onClick={handleSavePlan} disabled={saving || planSel === modal?.admin?.PlanId}>
                        {saving ? <Spinner size="sm" animation="border" /> : 'Confirmar Cambio'}
                    </button>
                </Modal.Footer>
            </Modal>

            {/* ── Modal: Extend Trial ── */}
            <Modal show={modal?.type === 'trial'} onHide={closeModal} centered dialogClassName="sa-modal">
                <Modal.Header closeButton className="border-0">
                    <Modal.Title style={{ fontWeight: 800, fontSize: '1.1rem', color: '#f8fafc' }}>
                        <FontAwesomeIcon icon={faClock} style={{ color: 'var(--sa-gold)', marginRight: '0.5rem' }} />
                        Extender Trial — {modal?.admin?.AdminNombre}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: '1.5rem' }}>
                        Seleccioná cuántos días adicionales deseas agregar al periodo de prueba.
                    </p>
                    <div className="d-flex flex-wrap gap-2">
                        {TRIAL_OPTS.map(opt => (
                            <button key={opt.days}
                                className={`sa-btn ${trialDays === opt.days ? 'sa-btn-primary' : 'sa-btn-ghost'}`}
                                onClick={() => setTrialDays(opt.days)}
                                style={{ flex: '1 0 calc(50% - 0.5rem)' }}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <button className="sa-btn sa-btn-ghost" onClick={closeModal}>Cancelar</button>
                    <button className="sa-btn sa-btn-amber" onClick={handleExtendTrial} disabled={saving}>
                        {saving ? <Spinner size="sm" animation="border" /> : `Agregar ${trialDays} días`}
                    </button>
                </Modal.Footer>
            </Modal>

            {/* ── Modal: Toggle Status ── */}
            <Modal show={modal?.type === 'status'} onHide={closeModal} centered dialogClassName="sa-modal">
                <Modal.Header closeButton className="border-0">
                    <Modal.Title style={{ fontWeight: 800, fontSize: '1.1rem', color: '#f8fafc' }}>
                        <FontAwesomeIcon icon={modal?.admin?.AdminActivo ? faBan : faCheckCircle}
                            style={{ color: modal?.admin?.AdminActivo ? 'var(--sa-danger)' : 'var(--sa-success)', marginRight: '0.5rem' }} />
                        {modal?.admin?.AdminActivo ? 'Suspender' : 'Reactivar'} — {modal?.admin?.AdminNombre}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p style={{ color: '#94a3b8', fontSize: '0.9rem' }}>
                        {modal?.admin?.AdminActivo
                            ? 'El administrador perderá acceso inmediatamente al panel de TuFila. Esta acción es reversible.'
                            : 'El administrador recuperará el acceso completo al panel de TuFila.'
                        }
                    </p>
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <button className="sa-btn sa-btn-ghost" onClick={closeModal}>Cancelar</button>
                    <button
                        className={`sa-btn ${modal?.admin?.AdminActivo ? 'sa-btn-danger' : 'sa-btn-success'}`}
                        onClick={handleToggleStatus} disabled={saving}
                    >
                        {saving ? <Spinner size="sm" animation="border" /> : (modal?.admin?.AdminActivo ? 'Suspender' : 'Reactivar')}
                    </button>
                </Modal.Footer>
            </Modal>

            {/* ── Modal: Assign Profile ── */}
            <Modal show={modal?.type === 'profile'} onHide={closeModal} centered dialogClassName="sa-modal">
                <Modal.Header closeButton className="border-0">
                    <Modal.Title style={{ fontWeight: 800, fontSize: '1.1rem', color: '#f8fafc' }}>
                        <FontAwesomeIcon icon={faShieldAlt} style={{ color: 'var(--sa-primary)', marginRight: '0.5rem' }} />
                        Asignar Perfil — {modal?.admin?.AdminNombre}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: '1.5rem' }}>
                        Seleccioná el perfil de permisos que tendrá este administrador.
                    </p>
                    <div className="d-flex flex-column gap-2">
                        <select
                            className="sa-input"
                            value={profileSel}
                            onChange={e => setProfileSel(e.target.value)}
                        >
                            <option value="">— Sin Perfil —</option>
                            {profiles.map(p => (
                                <option key={p.ProfileId} value={p.ProfileId}>{p.ProfileName}</option>
                            ))}
                        </select>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <button className="sa-btn sa-btn-ghost" onClick={closeModal}>Cancelar</button>
                    <button className="sa-btn sa-btn-primary" onClick={handleSaveProfile} disabled={saving}>
                        {saving ? <Spinner size="sm" animation="border" /> : 'Guardar Perfil'}
                    </button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default SuperAdminUsers;
